"use client";

import CountrySelectorGenerator from "@/components/NumberGenerator/CountrySelectorGenerator";
import React, { useState, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Hyphenated } from "@/utils/Hyphenated";

export default function RentPrivateNumberSelectorClient({ countries }) {
  const router = useRouter();
  const pathname = usePathname();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(false);
  }, [pathname]);

  const handleCountryClick = (country) => {
    setSelectedCountry(country);
  };

  const handleSearch = () => {
    if (!selectedCountry || isLoading) {
      return;
    }

    setIsLoading(true);

    const countrySlug = Hyphenated(selectedCountry.country_name);
    router.push(`/rent-private-number/${countrySlug}`);
  };

  return (
    <>
      <div className="bg-gradient-to-tr to-[#090EDB] from-[#E73DC4] rounded-2xl py-12 md:py-16 lg:py-20 px-4 md:px-8">
        <div className="max-w-2xl mx-auto w-full">
          {/* Header Section */}
          <div className="text-center mb-8 md:mb-10 space-y-3">
            <h1 className="font-bold text-3xl md:text-4xl lg:text-5xl text-white leading-tight">
              Rent A Private{selectedCountry ? ` ${selectedCountry.country_name}` : ""} Phone Number (Non-Voip Phone Number)
            </h1>
            <p className="text-gray-100 text-sm md:text-base font-medium max-w-lg mx-auto">
            Need a dedicated private phone number for regular SMS verifications? Rent a private non-voip phone number and keep full control.
            <br /><br />
            We provide you with your own private virtual number that is never
            assigned to anyone else. After you finish using it, the number and
            all data are immediately <span className="text-white">deleted</span>{" "}
            and cannot be recovered.
            </p>
          </div>

          {/* Form Section */}
          <div className="space-y-4">
            {/* Country Selector */}
            <div className="w-full max-w-md mx-auto">
              <label className="block text-white/90 text-sm font-semibold mb-2">
                Select Country
              </label>
              <CountrySelectorGenerator
                data={countries}
                value={selectedCountry}
                handleMenuItemClick={handleCountryClick}
              />
            </div>

            {/* Search Button */}
            <div className="w-full max-w-md mx-auto pt-2">
              <button
                onClick={handleSearch}
                disabled={!selectedCountry || isLoading}
                className="w-full bg-gradient-to-tr from-[#090EDB] to-[#E73DC4] hover:from-[#090EDB]/90 hover:to-[#E73DC4]/90 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold text-lg py-4 px-6 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl active:scale-[0.98] flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2.5}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                    />
                  </svg>
                )}
                {isLoading ? "Loading..." : "View Numbers"}
              </button>
            </div>
          </div>

          {/* Status Message */}
          {selectedCountry && (
            <div className="mt-6 text-center">
              <p className="text-white/90 text-sm md:text-base font-medium">
                Viewing private numbers for{" "}
                <span className="font-bold">
                  {selectedCountry.country_flag} {selectedCountry.country_name}
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
